import { rest } from 'msw';
import { AGENTS_API_URL } from '../../config';

/*
 * Handler de MSW para el stream de v3 (`/v3/logistics/assistant/stream`).
 *
 * Por qué existe: los handlers mockeaban solo `/v2/logistics/assistant`, así que con
 * NODE_ENV=local el POST del stream de v3 quedaba sin interceptar — MSW tiraba
 * "captured a request without a matching request handler" y el chat mostraba el
 * lorem ipsum de los fixtures de conversación en lugar de una respuesta.
 *
 * Para qué sirve: es el banco de pruebas de QS-19224. La respuesta canónica de abajo
 * contiene TODOS los elementos de markdown que toca la épica — título, subtítulos,
 * párrafo, tabla de 8 columnas, tabla de 2, lista, negritas, link, código inline y en
 * bloque, cita y separador — para poder validar el render y los dos temas sin depender
 * del backend, de la latencia ni de que haya datos en la operación.
 *
 * Palabras clave en la consulta para pedir un caso puntual:
 *   "tabla"  → solo la tabla de 8 columnas (el peor caso de ancho)
 *   "corta"  → una respuesta corta sin jerarquía (el 44% de la producción real)
 *   cualquier otra cosa → la respuesta completa
 *
 * Limitación conocida: MSW 1.x entrega el body de una sola vez, así que los tokens
 * llegan todos juntos. Para el ojo es el peor caso del typewriter (C-2), que es
 * justamente lo que conviene ver antes y después de sacarlo.
 */

const TABLA_8_COLUMNAS = `| Código de ruta | Código de vehículo | Patente | Última medición | Temperatura mínima | Temperatura máxima | Límite inferior | Límite superior |
|---|---|---|---|---|---|---|---|
| ANDES018002026072204 | ANDES018 - BKTR41 | BKTR41 | 18.8°C | 18.8°C | 18.8°C | 0°C | 8°C |
| LIMAY52500002026072201 | LIMAY525 - CDHL72 | CDHL72 | 14.6°C | 14.6°C | 14.6°C | 0°C | 8°C |
| MAIPO110002026072201 | MAIPO110 - FMQS31 | FMQS31 | 14.3°C | 2.7°C | 14.3°C | 0°C | 8°C |
| PEHUE101002026072201 | PEHUE101 - NR3181 (RAMPLA) | NR3181 (RAMPLA) | 13°C | 13°C | 13°C | 0°C | 8°C |
| ANDES130002026072201 | ANDES130 - LWXB42 | LWXB42 | 13.5°C | -1.4°C | 13.5°C | 0°C | 8°C |`;

const RESPUESTA_COMPLETA = `El análisis de la operación indica un avance sostenido de la jornada, con la mayoría de las rutas todavía en ejecución.

# Resumen operacional del 22 de agosto

## Estado de las Rutas

De las **192 rutas** del día, 85 están en curso y 107 finalizadas. De las 85 en curso, 82 reportan temperatura.

## Histórico de temperaturas

${TABLA_8_COLUMNAS}

## Estado de los Pedidos

- ✅ Entregados: 1.204
- 🔄 En curso: 318
- ❌ Rechazados: 27
- Pendientes de asignación: 12

## Tasa de éxito en entregas

| Indicador | Valor |
|---|---|
| Efectividad | **4.8%** |
| Rutas activas | 49.2% |

El identificador de la ruta se construye como \`CODIGO + AAAAMMDD + SECUENCIA\`, por ejemplo:

\`\`\`
ANDES018 + 20260722 + 04  ->  ANDES018002026072204
\`\`\`

> Los vehículos fuera de rango se revisan contra el certificado de calibración.

---

## Conclusiones:

La jornada se encuentra en su fase inicial de ejecución, **lo que explica que el 95.1% de los pedidos permanezcan en estado pendiente** y la tasa de efectividad actual se ubique en un 4.8%. Con el 49.2% de las rutas activas, **se espera un incremento sostenido** en el volumen de visitas durante las próximas horas.

Podés ver el detalle en el [panel de seguimiento](https://www.quadminds.com).`;

const SOLO_TABLA = `De las 192 rutas del día de hoy, 85 están en curso y 82 tienen datos de temperatura.

# Informe de temperatura - Rutas en curso

${TABLA_8_COLUMNAS}`;

const RESPUESTA_CORTA = `La ruta **ANDES018002026072204** registró su última temperatura a las 14:32, con un valor de **18.8°C**, por encima del límite superior de 8°C.`;

function elegirRespuesta(query: string): string {
    const q = (query || '').toLowerCase();
    if (q.includes('tabla')) return SOLO_TABLA;
    if (q.includes('corta')) return RESPUESTA_CORTA;
    return RESPUESTA_COMPLETA;
}

/** Parte el texto en frames `event: token` como los que emite el nodo formatter. */
function armarSse(texto: string): string {
    const frames: string[] = [];
    // Trozos de ~40 caracteres para parecerse al tamaño de token real del formatter
    for (let i = 0; i < texto.length; i += 40) {
        frames.push(`event: token\ndata: ${JSON.stringify({ content: texto.slice(i, i + 40) })}\n\n`);
    }
    frames.push(`event: done\ndata: ${JSON.stringify({ status: 'completed', metadata: {} })}\n\n`);
    return frames.join('');
}

export const logisticsStreamHandler = [
    rest.post(AGENTS_API_URL + '/v3/logistics/assistant/stream', async (req, res, ctx) => {
        const body = (await req.json().catch(() => ({}))) as { query?: string; message?: string };
        const texto = elegirRespuesta(body.query ?? body.message ?? '');

        return res(
            ctx.status(200),
            ctx.set('Content-Type', 'text/event-stream'),
            ctx.set('Cache-Control', 'no-cache'),
            ctx.body(armarSse(texto)),
        );
    }),
];
